import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = '';
const COLORS = ['#1a73e8','#2e7d32','#c62828','#7b1fa2','#e67700','#0288d1','#d84315','#1565c0','#33691e','#4a148c'];
const DB_TYPES = ['postgresql','mysql','mssql'];

function ErdAgent() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [tables, setTables] = useState([]);
  const [relations, setRelations] = useState([]);
  const [positions, setPositions] = useState({});
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({x:0,y:0});
  const [selectedTable, setSelectedTable] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [dbType, setDbType] = useState('postgresql');
  const [ddl, setDdl] = useState('');
  const [analysis, setAnalysis] = useState('');
  const [showPanel, setShowPanel] = useState('tables');
  const [showProjects, setShowProjects] = useState(true);
  const [newTableName, setNewTableName] = useState('');
  // Reverse engineer
  const [revConfig, setRevConfig] = useState({type:'postgresql',host:'',port:5432,database:'',user:'',password:''});
  const [revLoading, setRevLoading] = useState(false);

  useEffect(() => { loadProjects(); }, []);

  const loadProjects = async () => {
    const res = await fetch(`${API}/erd/projects`);
    const data = await res.json();
    setProjects(data.projects || []);
  };

  const createProject = async () => {
    const name = prompt('프로젝트 이름을 입력하세요:', '새 ERD 프로젝트');
    if (!name) return;
    const res = await fetch(`${API}/erd/projects`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({name})});
    const data = await res.json();
    loadProjects();
    loadProject(data.id);
  };

  const loadProject = async (id) => {
    const res = await fetch(`${API}/erd/projects/${id}`);
    const data = await res.json();
    setProject(data);
    setTables(data.tables || []);
    setRelations(data.relations || []);
    setPositions(data.positions || {});
    setShowProjects(false);
  };

  const saveProject = async () => {
    if (!project) return;
    await fetch(`${API}/erd/projects/${project.id}`, {method:'PUT',headers:{'Content-Type':'application/json'},
      body:JSON.stringify({name:project.name,description:project.description||'',tables,relations,positions})});
    alert('저장 완료');
  };

  const deleteProject = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await fetch(`${API}/erd/projects/${id}`, {method:'DELETE'});
    if (project?.id === id) { setProject(null); setTables([]); setRelations([]); }
    loadProjects();
  };

  // Table CRUD
  const addTable = () => {
    if (!newTableName.trim()) return;
    const name = newTableName.trim().toUpperCase();
    if (tables.find(t => t.name === name)) return alert('이미 존재하는 테이블입니다.');
    setTables([...tables, {name, logical_name:'', columns:[
      {name:'id',type:'SERIAL',pk:true,fk:'',nullable:false,comment:'Primary Key',logical_name:'식별자'},
      {name:'created_at',type:'TIMESTAMP',pk:false,fk:'',nullable:false,comment:'생성일시',logical_name:'생성일시'},
      {name:'updated_at',type:'TIMESTAMP',pk:false,fk:'',nullable:false,comment:'수정일시',logical_name:'수정일시'},
    ]}]);
    setPositions({...positions, [name]: {x: 50 + Math.random()*400, y: 50 + Math.random()*300}});
    setNewTableName('');
    setSelectedTable(name);
  };

  const deleteTable = (name) => {
    setTables(tables.filter(t => t.name !== name));
    setRelations(relations.filter(r => !r.from.startsWith(name+'.') && !r.to.startsWith(name+'.')));
    const p = {...positions}; delete p[name]; setPositions(p);
    if (selectedTable === name) setSelectedTable(null);
  };

  const addColumn = (tableName) => {
    setTables(tables.map(t => t.name === tableName ? {...t, columns:[...t.columns, {name:'new_col',type:'VARCHAR(255)',pk:false,fk:'',nullable:true,comment:'',logical_name:''}]} : t));
  };

  const updateColumn = (tableName, colIdx, field, value) => {
    setTables(tables.map(t => {
      if (t.name !== tableName) return t;
      const cols = [...t.columns];
      cols[colIdx] = {...cols[colIdx], [field]: value};
      return {...t, columns: cols};
    }));
  };

  const deleteColumn = (tableName, colIdx) => {
    setTables(tables.map(t => t.name === tableName ? {...t, columns: t.columns.filter((_,i) => i !== colIdx)} : t));
  };

  const addRelation = (from, to, type) => {
    setRelations([...relations, {from, to, type: type || '1:N'}]);
  };

  const deleteRelation = (idx) => {
    setRelations(relations.filter((_,i) => i !== idx));
  };

  // AI
  const aiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const res = await fetch(`${API}/erd/ai-generate`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({prompt:aiPrompt,db_type:dbType})});
      const data = await res.json();
      if (data.tables) {
        setTables(data.tables);
        setRelations(data.relations || []);
        const pos = {};
        data.tables.forEach((t,i) => { pos[t.name] = {x: 50+(i%4)*280, y: 50+Math.floor(i/4)*300}; });
        setPositions(pos);
        setAiPrompt('');
      } else { alert(data.error || 'AI 생성 실패'); }
    } catch (e) { alert('오류'); }
    setAiLoading(false);
  };

  const aiAnalyze = async () => {
    setAiLoading(true);
    try {
      const res = await fetch(`${API}/erd/ai-analyze`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({tables,relations})});
      const data = await res.json();
      setAnalysis(data.analysis || '');
      setShowPanel('analysis');
    } catch (e) {}
    setAiLoading(false);
  };

  const generateDDL = async () => {
    const res = await fetch(`${API}/erd/generate-ddl`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({tables,relations,db_type:dbType})});
    const data = await res.json();
    setDdl(data.ddl || '');
    setShowPanel('ddl');
  };

  const reverseEngineer = async () => {
    setRevLoading(true);
    try {
      const res = await fetch(`${API}/erd/reverse-engineer`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(revConfig)});
      const data = await res.json();
      if (data.tables) {
        setTables(data.tables);
        setRelations(data.relations || []);
        setPositions(data.positions || {});
      } else { alert(data.error || '역공학 실패'); }
    } catch (e) { alert('연결 실패'); }
    setRevLoading(false);
  };

  // Canvas drawing
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width = canvas.parentElement.clientWidth;
    const H = canvas.height = Math.max(600, canvas.parentElement.clientHeight);
    ctx.clearRect(0, 0, W, H);

    // Grid
    ctx.strokeStyle = '#f0f0f0';
    ctx.lineWidth = 1;
    for (let x = 0; x < W; x += 30) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }
    for (let y = 0; y < H; y += 30) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }

    // Relations (lines)
    relations.forEach(rel => {
      const [ft, fc] = rel.from.split('.');
      const [tt, tc] = rel.to.split('.');
      const fp = positions[ft];
      const tp = positions[tt];
      if (!fp || !tp) return;
      const fx = fp.x + 120; const fy = fp.y + 30;
      const tx = tp.x; const ty = tp.y + 30;
      ctx.beginPath();
      ctx.strokeStyle = '#999';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.moveTo(fx, fy);
      ctx.bezierCurveTo(fx+60, fy, tx-60, ty, tx, ty);
      ctx.stroke();
      // Label
      const mx = (fx+tx)/2; const my = (fy+ty)/2;
      ctx.fillStyle = '#666';
      ctx.font = '11px Arial';
      ctx.fillText(rel.type, mx-10, my-6);
      // Arrow
      ctx.beginPath();
      ctx.fillStyle = '#999';
      ctx.moveTo(tx, ty);
      ctx.lineTo(tx-8, ty-4);
      ctx.lineTo(tx-8, ty+4);
      ctx.fill();
    });

    // Tables (boxes)
    tables.forEach((tbl, ti) => {
      const pos = positions[tbl.name] || {x:50,y:50};
      const W_TBL = 340;
      const colH = 22;
      const headerH = 32;
      const H_TBL = headerH + tbl.columns.length * colH + 4;
      const color = COLORS[ti % COLORS.length];
      const isSel = selectedTable === tbl.name;

      // Shadow
      ctx.fillStyle = 'rgba(0,0,0,0.08)';
      ctx.fillRect(pos.x+3, pos.y+3, W_TBL, H_TBL);

      // Body
      ctx.fillStyle = '#fff';
      ctx.strokeStyle = isSel ? color : '#ccc';
      ctx.lineWidth = isSel ? 3 : 1;
      ctx.beginPath();
      ctx.roundRect(pos.x, pos.y, W_TBL, H_TBL, 6);
      ctx.fill(); ctx.stroke();

      // Header
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.roundRect(pos.x, pos.y, W_TBL, headerH, [6,6,0,0]);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 13px Arial';
      const displayName = tbl.logical_name ? tbl.name + ' (' + tbl.logical_name + ')' : tbl.name;
      ctx.fillText(displayName, pos.x+10, pos.y+21);
      ctx.font = '10px Arial';
      ctx.fillText(tbl.columns.length + ' cols', pos.x + W_TBL - 45, pos.y+21);

      // Columns
      tbl.columns.forEach((col, ci) => {
        const cy = pos.y + headerH + ci * colH;
        if (ci % 2 === 0) {
          ctx.fillStyle = '#fafafa';
          ctx.fillRect(pos.x+1, cy, W_TBL-2, colH);
        }
        ctx.fillStyle = col.pk ? color : '#333';
        ctx.font = col.pk ? 'bold 11px Consolas' : '11px Consolas';
        const icon = col.pk ? 'PK ' : col.fk ? 'FK ' : '   ';
        ctx.fillText(icon + col.name, pos.x+8, cy+15);
        ctx.fillStyle = '#888';
        ctx.font = '10px Arial';
        const colLogical = col.logical_name || col.comment || '';
        if (colLogical) {
          ctx.fillText(colLogical, pos.x+140, cy+15);
        }
        ctx.fillStyle = '#aaa';
        ctx.font = '9px Arial';
        ctx.fillText(col.type, pos.x+260, cy+15);
      });
    });
  }, [tables, relations, positions, selectedTable]);

  useEffect(() => { draw(); }, [draw]);

  // Mouse handlers
  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    for (const tbl of tables) {
      const pos = positions[tbl.name];
      if (!pos) continue;
      if (mx >= pos.x && mx <= pos.x+340 && my >= pos.y && my <= pos.y+32) {
        setDragging(tbl.name);
        setDragOffset({x: mx-pos.x, y: my-pos.y});
        setSelectedTable(tbl.name);
        return;
      }
      if (mx >= pos.x && mx <= pos.x+340 && my >= pos.y && my <= pos.y+32+tbl.columns.length*22) {
        setSelectedTable(tbl.name);
        return;
      }
    }
    setSelectedTable(null);
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setPositions({...positions, [dragging]: {x: e.clientX-rect.left-dragOffset.x, y: e.clientY-rect.top-dragOffset.y}});
  };

  const handleMouseUp = () => { setDragging(null); };

  const selTbl = tables.find(t => t.name === selectedTable);

  return (
    <div style={{minHeight:'100vh',background:'#f5f7fa',display:'flex',flexDirection:'column'}}>
      <div style={{background:'linear-gradient(135deg,#4a148c,#7b1fa2,#9c27b0)',padding:'12px 20px',display:'flex',alignItems:'center',gap:12,boxShadow:'0 2px 12px rgba(74,20,140,0.3)'}}>
        <button onClick={() => navigate('/')} style={{background:'rgba(255,255,255,0.15)',border:'none',color:'white',width:36,height:36,borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12 4l-6 6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div style={{flex:1}}>
          <span style={{color:'white',fontSize:18,fontWeight:'bold'}}>AI ERD 설계</span>
          {project && <span style={{color:'rgba(255,255,255,0.7)',fontSize:13,marginLeft:12}}>{project.name}</span>}
        </div>
        <select value={dbType} onChange={e => setDbType(e.target.value)} style={{padding:'5px 10px',borderRadius:4,border:'1px solid rgba(255,255,255,0.3)',background:'rgba(255,255,255,0.15)',color:'white',fontSize:12}}>
          {DB_TYPES.map(d => <option key={d} value={d} style={{color:'#333'}}>{d.toUpperCase()}</option>)}
        </select>
        {project && <>
          <button onClick={saveProject} style={{padding:'6px 14px',background:'rgba(255,255,255,0.2)',border:'1px solid rgba(255,255,255,0.3)',color:'white',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>저장</button>
          <button onClick={() => setShowProjects(true)} style={{padding:'6px 14px',background:'rgba(255,255,255,0.2)',border:'1px solid rgba(255,255,255,0.3)',color:'white',borderRadius:6,cursor:'pointer',fontSize:12}}>프로젝트 목록</button>
        </>}
      </div>

      {showProjects ? (
        <div style={{maxWidth:800,margin:'40px auto',padding:'0 20px',width:'100%'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
            <h2 style={{margin:0,color:'#4a148c'}}>ERD 프로젝트</h2>
            <button onClick={createProject} style={{padding:'10px 20px',background:'#7b1fa2',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:14,fontWeight:'bold'}}>+ 새 프로젝트</button>
          </div>
          {projects.map(p => (
            <div key={p.id} style={{background:'white',borderRadius:8,border:'1px solid #ddd',padding:16,marginBottom:10,display:'flex',justifyContent:'space-between',alignItems:'center',cursor:'pointer'}}
              onClick={() => loadProject(p.id)}>
              <div>
                <div style={{fontWeight:'bold',fontSize:15}}>{p.name}</div>
                <div style={{fontSize:12,color:'#888'}}>{p.updated_at}</div>
              </div>
              <div style={{display:'flex',gap:6}}>
                <button onClick={(e) => {e.stopPropagation(); deleteProject(p.id);}} style={{padding:'4px 10px',background:'#ffebee',color:'#c62828',border:'1px solid #ef9a9a',borderRadius:4,cursor:'pointer',fontSize:11}}>삭제</button>
              </div>
            </div>
          ))}
          {projects.length === 0 && <p style={{textAlign:'center',color:'#999',padding:40}}>프로젝트가 없습니다. 새 프로젝트를 생성하세요.</p>}
        </div>
      ) : (
        <div style={{flex:1,display:'flex',overflow:'hidden'}}>
          {/* Left Panel */}
          <div style={{width:300,background:'white',borderRight:'1px solid #e0e0e0',display:'flex',flexDirection:'column',overflow:'hidden'}}>
            {/* AI Input */}
            <div style={{padding:12,borderBottom:'1px solid #eee',background:'#f3e8fd'}}>
              <div style={{fontSize:12,fontWeight:'bold',color:'#4a148c',marginBottom:6}}>AI로 ERD 생성</div>
              <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                placeholder="예: 회원, 주문, 상품, 리뷰 테이블 만들어줘"
                style={{width:'100%',height:50,padding:8,borderRadius:4,border:'1px solid #ddd',fontSize:12,boxSizing:'border-box',resize:'none'}} />
              <div style={{display:'flex',gap:6,marginTop:6}}>
                <button onClick={aiGenerate} disabled={aiLoading} style={{flex:1,padding:'5px',background:aiLoading?'#ccc':'#7b1fa2',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:'bold'}}>
                  {aiLoading ? '생성중...' : 'AI 생성'}
                </button>
                <button onClick={aiAnalyze} disabled={aiLoading || tables.length===0} style={{flex:1,padding:'5px',background:'#e67700',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:'bold'}}>분석/개선</button>
                <button onClick={generateDDL} disabled={tables.length===0} style={{flex:1,padding:'5px',background:'#1565c0',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:'bold'}}>DDL</button>
              </div>
            </div>

            {/* Tabs */}
            <div style={{display:'flex',borderBottom:'1px solid #eee'}}>
              {[{k:'tables',l:'테이블'},{k:'relations',l:'관계'},{k:'reverse',l:'역공학'},{k:'ddl',l:'DDL'},{k:'analysis',l:'분석'}].map(t => (
                <button key={t.k} onClick={() => setShowPanel(t.k)} style={{flex:1,padding:'8px 4px',border:'none',cursor:'pointer',fontSize:11,fontWeight:'bold',
                  background:showPanel===t.k?'#f3e8fd':'white',color:showPanel===t.k?'#7b1fa2':'#888',
                  borderBottom:showPanel===t.k?'2px solid #7b1fa2':'2px solid transparent'}}>{t.l}</button>
              ))}
            </div>

            {/* Panel Content */}
            <div style={{flex:1,overflowY:'auto',padding:10}}>
              {showPanel === 'tables' && (
                <>
                  <div style={{display:'flex',gap:6,marginBottom:10}}>
                    <input value={newTableName} onChange={e => setNewTableName(e.target.value.toUpperCase())}
                      onKeyDown={e => {if(e.key==='Enter') addTable();}}
                      placeholder="테이블명" style={{flex:1,padding:6,borderRadius:4,border:'1px solid #ddd',fontSize:12,fontFamily:'monospace'}} />
                    <button onClick={addTable} style={{padding:'6px 12px',background:'#7b1fa2',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:11}}>추가</button>
                  </div>
                  {tables.map((tbl,ti) => (
                    <div key={tbl.name} style={{marginBottom:8,borderRadius:6,border:selectedTable===tbl.name?'2px solid '+COLORS[ti%COLORS.length]:'1px solid #e0e0e0',overflow:'hidden'}}>
                      <div onClick={() => setSelectedTable(selectedTable===tbl.name?null:tbl.name)}
                        style={{padding:'6px 10px',background:COLORS[ti%COLORS.length],color:'white',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,fontWeight:'bold'}}>
                        <span>{tbl.name} {tbl.logical_name ? <span style={{fontWeight:'normal',fontSize:11,opacity:0.85}}>({tbl.logical_name})</span> : ''} <span style={{fontWeight:'normal',fontSize:10,opacity:0.7}}>({tbl.columns.length})</span></span>
                        <button onClick={(e) => {e.stopPropagation();deleteTable(tbl.name);}} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'white',borderRadius:4,cursor:'pointer',fontSize:10,padding:'2px 6px'}}>X</button>
                      </div>
                      {selectedTable === tbl.name && (
                        <div style={{padding:8,background:'#fafafa'}}>
                          {tbl.columns.map((col,ci) => (
                            <div key={ci} style={{display:'grid',gridTemplateColumns:'80px 70px 20px 20px',gap:4,marginBottom:4,alignItems:'center'}}>
                              <input value={col.name} onChange={e => updateColumn(tbl.name,ci,'name',e.target.value)} style={{padding:3,border:'1px solid #ddd',borderRadius:3,fontSize:10,fontFamily:'monospace'}} />
                              <input value={col.type} onChange={e => updateColumn(tbl.name,ci,'type',e.target.value)} style={{padding:3,border:'1px solid #ddd',borderRadius:3,fontSize:10}} />
                              <input type="checkbox" checked={col.pk} onChange={e => updateColumn(tbl.name,ci,'pk',e.target.checked)} title="PK" />
                              <button onClick={() => deleteColumn(tbl.name,ci)} style={{background:'#ffebee',border:'none',color:'#c62828',borderRadius:3,cursor:'pointer',fontSize:10}}>-</button>
                            </div>
                          ))}
                          <button onClick={() => addColumn(tbl.name)} style={{width:'100%',padding:4,background:'#e8f5e9',border:'1px solid #c8e6c9',borderRadius:4,cursor:'pointer',fontSize:11,color:'#2e7d32'}}>+ 컬럼 추가</button>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}

              {showPanel === 'relations' && (
                <>
                  {relations.map((r,i) => (
                    <div key={i} style={{padding:8,marginBottom:6,background:'#fafafa',borderRadius:6,border:'1px solid #eee',fontSize:11,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span><strong>{r.from}</strong> → <strong>{r.to}</strong> [{r.type}]</span>
                      <button onClick={() => deleteRelation(i)} style={{background:'#ffebee',border:'none',color:'#c62828',borderRadius:4,cursor:'pointer',fontSize:10,padding:'2px 6px'}}>삭제</button>
                    </div>
                  ))}
                  <div style={{fontSize:11,color:'#888',marginTop:8}}>관계 추가: AI 생성 또는 역공학으로 자동 생성됩니다.</div>
                </>
              )}

              {showPanel === 'reverse' && (
                <div style={{fontSize:12}}>
                  <div style={{marginBottom:8}}>
                    <label style={{fontSize:11,color:'#888'}}>DB 종류</label>
                    <select value={revConfig.type} onChange={e => setRevConfig({...revConfig,type:e.target.value})} style={{width:'100%',padding:6,borderRadius:4,border:'1px solid #ddd',fontSize:12}}>
                      <option value="postgresql">PostgreSQL</option>
                      <option value="mysql">MySQL</option>
                    </select>
                  </div>
                  {['host','port','database','user'].map(f => (
                    <div key={f} style={{marginBottom:6}}>
                      <label style={{fontSize:11,color:'#888'}}>{f}</label>
                      <input value={revConfig[f]} onChange={e => setRevConfig({...revConfig,[f]:e.target.value})}
                        style={{width:'100%',padding:6,borderRadius:4,border:'1px solid #ddd',fontSize:12,boxSizing:'border-box'}} />
                    </div>
                  ))}
                  <div style={{marginBottom:8}}>
                    <label style={{fontSize:11,color:'#888'}}>password</label>
                    <input type="password" value={revConfig.password} onChange={e => setRevConfig({...revConfig,password:e.target.value})}
                      style={{width:'100%',padding:6,borderRadius:4,border:'1px solid #ddd',fontSize:12,boxSizing:'border-box'}} />
                  </div>
                  <button onClick={reverseEngineer} disabled={revLoading} style={{width:'100%',padding:8,background:revLoading?'#ccc':'#7b1fa2',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:13,fontWeight:'bold'}}>
                    {revLoading ? '분석중...' : 'DB → ERD 역공학'}
                  </button>
                </div>
              )}

              {showPanel === 'ddl' && ddl && (
                <div>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                    <span style={{fontSize:12,fontWeight:'bold',color:'#1565c0'}}>{dbType.toUpperCase()} DDL</span>
                    <button onClick={() => {navigator.clipboard.writeText(ddl);alert('복사됨');}} style={{padding:'3px 8px',background:'#1565c0',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:10}}>복사</button>
                  </div>
                  <pre style={{background:'#f5f5f5',padding:10,borderRadius:6,fontSize:11,overflowX:'auto',whiteSpace:'pre-wrap',maxHeight:400}}>{ddl}</pre>
                </div>
              )}

              {showPanel === 'analysis' && analysis && (
                <div style={{fontSize:13,lineHeight:1.7}}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                    h3: ({children}) => <h3 style={{fontSize:14,color:'#7b1fa2',marginTop:10}}>{children}</h3>,
                    strong: ({children}) => <strong style={{color:'#4a148c'}}>{children}</strong>,
                    li: ({children}) => <li style={{marginBottom:3}}>{children}</li>,
                  }}>{analysis}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>

          {/* Canvas */}
          <div style={{flex:1,overflow:'auto',background:'#fafafa',position:'relative'}}>
            <canvas ref={canvasRef}
              onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
              style={{cursor:dragging?'grabbing':'default',display:'block'}} />
            {tables.length === 0 && (
              <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',textAlign:'center',color:'#999'}}>
                <div style={{fontSize:48,marginBottom:12}}>
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><rect x="8" y="8" width="20" height="20" rx="4" fill="#f3e8fd" stroke="#7b1fa2" strokeWidth="2"/><rect x="36" y="8" width="20" height="20" rx="4" fill="#f3e8fd" stroke="#7b1fa2" strokeWidth="2"/><rect x="22" y="36" width="20" height="20" rx="4" fill="#f3e8fd" stroke="#7b1fa2" strokeWidth="2"/><path d="M28 18h8M18 28v8M46 28v8" stroke="#7b1fa2" strokeWidth="2"/></svg>
                </div>
                <div style={{fontSize:16,fontWeight:'bold'}}>AI로 ERD를 시작하세요</div>
                <div style={{fontSize:13,marginTop:6}}>왼쪽 패널에서 "회원, 주문, 상품 테이블 만들어줘"</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ErdAgent;
